import React, { useState, useCallback, useRef } from 'react';
import { Button } from 'react-bootstrap';
import { FaChevronDown, FaPlus } from 'react-icons/fa';
import { ramBotService } from '../../services/ramBot.service';
import { runbookVersionService } from '../../services/runbookVersion.service';
import RamBotChatPanel from './RamBotChatPanel';
import RamBotRunbookViewer from './RamBotRunbookViewer';
import ramImg from './ramLogo.jpeg';
import '../workflow/aibuilder/AIBuilderWorkspace.css'; // shared .ai-chat-*/.ai-bubble-* styles
import './RamBotBubble.css';

const REQUEST_TIMEOUT_MS = 60000;
const DRAG_MARGIN = 8; // keep the window at least this far from each viewport edge
const DRAG_THRESHOLD = 4; // px of movement before a press counts as a drag (vs a click)
const FAB_SIZE = 66; // must match .ram-bot-fab width/height
const PANEL_W = 440; // must match .ram-bot-panel's clamp() ceiling width
const PANEL_H = 560; // must match .ram-bot-panel's clamp() ceiling height
const MIN_W = 320; // minimum panel width when resizing — must match the clamp() floor
const MIN_H = 340; // minimum panel height when resizing — must match the clamp() floor

const clamp = (v, lo, hi) => Math.max(lo, Math.min(v, hi));

// Mirrors .ram-bot-panel's CSS `clamp(MIN, vw/vh %, PANEL)` sizing in JS, so a position
// computed before the panel has ever rendered (nothing to measure yet) still matches what the
// CSS actually renders instead of assuming the fixed ceiling size unconditionally.
const defaultPanelSize = () => ({
  w: clamp(window.innerWidth * 0.32, MIN_W, PANEL_W),
  h: clamp(window.innerHeight * 0.45, MIN_H, PANEL_H),
});

// Given the icon's top-left, compute where the expanded panel's top-left should go so it
// "explodes" toward the open space — up-and-left when the icon is tucked bottom-right (the
// common case), so a wide panel never overflows off-screen. Returns null when the icon is at
// its default corner (panel then uses the CSS default bottom-right anchor).
function explodedPanelPos(iconPos, size) {
  if (!iconPos) return null;
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const fallback = defaultPanelSize();
  const w = Math.min(size?.w || fallback.w, vw - 2 * DRAG_MARGIN);
  const h = Math.min(size?.h || fallback.h, vh - 2 * DRAG_MARGIN);
  const expandLeft = iconPos.x + FAB_SIZE / 2 > vw / 2;
  const expandUp = iconPos.y + FAB_SIZE / 2 > vh / 2;
  const x = expandLeft ? iconPos.x + FAB_SIZE - w : iconPos.x;
  const y = expandUp ? iconPos.y + FAB_SIZE - h : iconPos.y;
  return {
    x: clamp(x, DRAG_MARGIN, vw - w - DRAG_MARGIN),
    y: clamp(y, DRAG_MARGIN, vh - h - DRAG_MARGIN),
  };
}

// RamBot — a draggable floating window on the Dashboard (home screen), for cross-client
// "which clients use which systems/objects" questions (#2448). Unlike CoryBot/GraceBot there is
// no runbook canvas and no server-owned conversationId: entirely stateless per request, so on
// every send we resend the visible transcript as priorMessages. Caller (Dashboard.jsx) is
// responsible for only rendering this for SuperUser/ProductTeam users (#2454) -- this component
// does not re-check, matching how KbBuilderBubble/GraceBotBubble assume their mount point already
// gated visibility.
// Static opening line -- not a real model turn, so it's tagged kind: 'greeting' and excluded
// from priorMessages (no reason to spend tokens re-sending a canned line back to the model).
const GREETING = "Hi, I'm Ram. I can find existing runbooks by system and system object — what runbook are you looking for?";
const greetingMessage = () => ({ role: 'assistant', kind: 'greeting', content: GREETING });

function RamBotBubble() {
  const [open, setOpen] = useState(false); // starts minimized to the bubble
  const [messages, setMessages] = useState(() => [greetingMessage()]);
  const [inFlight, setInFlight] = useState(false);
  const [exporting, setExporting] = useState(false);
  // Runbook currently open in the large view-only popup ({runbookId, runbookGuid} | null).
  const [viewerTarget, setViewerTarget] = useState(null);
  // Two independent positions (top-left, viewport px; null => default corner via CSS): the
  // minimized bubble and the open panel. Keeping them separate lets the bubble stay where you
  // left it while the panel explodes toward open space on open.
  const [iconPos, setIconPos] = useState(null);
  const [panelPos, setPanelPos] = useState(null);
  const [size, setSize] = useState(null); // {w, h} once resized; null => CSS default
  const windowRef = useRef(null);
  const dragOffset = useRef(null);
  const movedRef = useRef(false); // did the last press move far enough to be a drag?

  const handleDragStart = useCallback((e) => {
    if (e.button !== 0 || !windowRef.current) return;
    const setPosition = open ? setPanelPos : setIconPos;
    const rect = windowRef.current.getBoundingClientRect();
    dragOffset.current = { dx: e.clientX - rect.left, dy: e.clientY - rect.top };
    const startX = e.clientX;
    const startY = e.clientY;
    movedRef.current = false;
    e.preventDefault();

    const onMove = (ev) => {
      const off = dragOffset.current;
      const el = windowRef.current;
      if (!off || !el) return;
      if (!movedRef.current && Math.abs(ev.clientX - startX) + Math.abs(ev.clientY - startY) > DRAG_THRESHOLD) {
        movedRef.current = true;
      }
      if (!movedRef.current) return;
      const w = el.offsetWidth;
      const h = el.offsetHeight;
      const x = clamp(ev.clientX - off.dx, DRAG_MARGIN, window.innerWidth - w - DRAG_MARGIN);
      const y = clamp(ev.clientY - off.dy, DRAG_MARGIN, window.innerHeight - h - DRAG_MARGIN);
      setPosition({ x, y });
    };
    const onUp = () => {
      dragOffset.current = null;
      document.body.style.userSelect = '';
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
    document.body.style.userSelect = 'none';
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  }, [open]);

  // Open from the bubble: explode the panel toward open space so it never renders off-screen.
  const openPanel = useCallback(() => {
    setPanelPos(explodedPanelPos(iconPos, size));
    setOpen(true);
  }, [iconPos, size]);

  // Resize from the top-left grip, keeping the panel's bottom-right corner fixed so it grows
  // up-and-left (matching its default bottom-right anchor).
  const handleResizeStart = useCallback((e) => {
    if (e.button !== 0 || !windowRef.current) return;
    e.preventDefault();
    e.stopPropagation();
    const rect = windowRef.current.getBoundingClientRect();
    const brX = rect.right;
    const brY = rect.bottom;

    const onMove = (ev) => {
      const w = clamp(brX - ev.clientX, MIN_W, brX - DRAG_MARGIN);
      const h = clamp(brY - ev.clientY, MIN_H, brY - DRAG_MARGIN);
      setSize({ w, h });
      setPanelPos({ x: brX - w, y: brY - h });
    };
    const onUp = () => {
      document.body.style.userSelect = '';
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
    document.body.style.userSelect = 'none';
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  }, []);

  const iconStyle = iconPos ? { left: iconPos.x, top: iconPos.y, right: 'auto', bottom: 'auto' } : undefined;
  const panelStyle = (panelPos || size)
    ? {
        ...(panelPos ? { left: panelPos.x, top: panelPos.y, right: 'auto', bottom: 'auto' } : {}),
        ...(size ? { width: size.w, height: size.h } : {}),
      }
    : undefined;

  const startNewConversation = useCallback(() => {
    setMessages([greetingMessage()]);
  }, []);

  const send = useCallback(async (prompt) => {
    const userBubble = { role: 'user', content: prompt };
    setMessages((prev) => [...prev, userBubble]);
    setInFlight(true);

    let timeoutId;
    const timeoutPromise = new Promise((_, reject) => {
      timeoutId = setTimeout(
        () => reject(new Error('RamBot took too long — try again.')),
        REQUEST_TIMEOUT_MS
      );
    });

    try {
      // Stateless per request (no server-owned conversationId) -- resend the visible transcript
      // (before this turn's user bubble) as priorMessages, excluding error bubbles and the
      // canned greeting (never a real model turn, nothing to re-send).
      const priorMessages = messages
        .filter((m) => m.role === 'user' || (m.role === 'assistant' && m.kind !== 'error' && m.kind !== 'greeting'))
        .map((m) => ({ role: m.role, content: m.content }));

      const data = await Promise.race([
        ramBotService.ask({ prompt, priorMessages }),
        timeoutPromise,
      ]);
      clearTimeout(timeoutId);

      setMessages((prev) => [
        ...prev,
        {
          role: 'assistant',
          content: data?.answer || 'No answer returned.',
          // Structured tool data (client groups, systemObjects, ...) for RamBotResultsWidget --
          // null/undefined when no tool call succeeded, in which case the widget renders nothing.
          toolName: data?.toolName,
          data: data?.data,
        },
      ]);
    } catch (err) {
      clearTimeout(timeoutId);
      const serverMessage = err?.response?.data?.error || err?.response?.data?.message;
      const errText = serverMessage || err?.message || 'Something went wrong. Try again.';
      setMessages((prev) => [...prev, { role: 'assistant', kind: 'error', content: errText }]);
    } finally {
      setInFlight(false);
    }
  }, [messages]);

  // Drill-down from a runbook chip/link: pops the large view-only viewer (#2461-adjacent --
  // genuinely can't be edited from here) rather than re-asking RamBot as a chat turn. clientId is
  // accepted but unused today -- kept in the signature so a later iteration can switch client
  // context and hand off to the real designer (CoryBot) for edits, without changing
  // RamBotResultsWidget's call site.
  const handleOpenRunbook = useCallback((runbookId, runbookGuid /* , clientId */) => {
    setViewerTarget({ runbookId, runbookGuid });
  }, []);

  const closeViewer = useCallback(() => setViewerTarget(null), []);

  // A "Narrow it down" suggestion chip -- just sends its text as the next chat turn.
  const handleSendSuggestion = useCallback((suggestionText) => {
    send(suggestionText);
  }, [send]);

  // Export a runbook found via RamBot as a downloadable JSON file, without opening it in the
  // designer. Reuses the exact endpoint WorkflowBuilder's own "Export Workflow" is built on
  // (GET /api/runbook-version) -- picks the current (highest VersionNum, #1977 Apply model) live
  // version and downloads its RunbookString. Same safety as that feature: RunbookString's
  // connectors are always the stripped 4-field shape (connectorId/connectorNumber/system/
  // description) -- the canvas that produced it never held credentials in the first place, so
  // there's nothing to strip here.
  const handleExportRunbook = useCallback(async (runbookId, runbookGuid) => {
    if (!runbookGuid) {
      setMessages((prev) => [...prev, { role: 'assistant', kind: 'error', content: `Could not export ${runbookId} -- missing runbookGuid.` }]);
      return;
    }
    setExporting(true);
    try {
      const response = await runbookVersionService.get({ runbookId, runbookGuid });
      const versions = response.data;
      const current = (versions || []).reduce(
        (max, v) => (max === null || v.versionNum > max.versionNum ? v : max),
        null
      );
      if (!current?.runbookString) {
        throw new Error('No runbook definition found to export.');
      }

      const pretty = JSON.stringify(JSON.parse(current.runbookString), null, 2);
      const blob = new Blob([pretty], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${runbookId}.json`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      const serverMessage = err?.response?.data?.error || err?.response?.data?.message;
      setMessages((prev) => [
        ...prev,
        { role: 'assistant', kind: 'error', content: serverMessage || err?.message || `Could not export ${runbookId}. Try again.` },
      ]);
    } finally {
      setExporting(false);
    }
  }, []);

  // Rendered as a Modal (portal to document.body), so it overlays the whole screen regardless of
  // where the small floating panel sits or whether it's even open -- "pop up into a large window"
  // independent of the chat panel's own size.
  const viewer = (
    <RamBotRunbookViewer
      show={!!viewerTarget}
      runbookId={viewerTarget?.runbookId}
      runbookGuid={viewerTarget?.runbookGuid}
      onHide={closeViewer}
      onExport={handleExportRunbook}
      exporting={exporting}
    />
  );

  if (!open) {
    return (
      <>
        <button
          type="button"
          ref={windowRef}
          className="ram-bot-fab"
          style={iconStyle}
          onMouseDown={handleDragStart}
          onClick={() => {
            // Suppress the open if the press was actually a drag.
            if (movedRef.current) { movedRef.current = false; return; }
            openPanel();
          }}
          title="RamBot — click to open, drag to move"
          aria-label="Open RamBot"
        >
          <img src={ramImg} alt="RamBot" className="ram-bot-fab-img" draggable={false} />
        </button>
        {viewer}
      </>
    );
  }

  return (
    <div className="ram-bot-panel" ref={windowRef} style={panelStyle} role="dialog" aria-label="RamBot">
      <div className="ram-bot-resize-handle" onMouseDown={handleResizeStart} title="Drag to resize" aria-hidden="true" />
      <div className="ai-builder-header ram-bot-header d-flex align-items-center justify-content-between">
        <div className="d-flex align-items-center ram-bot-drag-handle" onMouseDown={handleDragStart} title="Drag to move">
          <img src={ramImg} alt="RamBot" className="ram-bot-avatar me-2" draggable={false} />
          <strong>RamBot</strong>
        </div>
        <div className="d-flex align-items-center">
          <Button
            variant="link"
            size="sm"
            onClick={startNewConversation}
            className="ai-builder-close p-0 me-3"
            title="New conversation"
            aria-label="New conversation"
          >
            <FaPlus />
          </Button>
          <Button
            variant="link"
            size="sm"
            onClick={() => setOpen(false)}
            className="ai-builder-close p-0"
            title="Minimize — keeps this conversation"
            aria-label="Minimize RamBot"
          >
            <FaChevronDown />
          </Button>
        </div>
      </div>
      <RamBotChatPanel
        messages={messages}
        inFlight={inFlight}
        onSend={send}
        onOpenRunbook={handleOpenRunbook}
        onSendSuggestion={handleSendSuggestion}
        onExportRunbook={handleExportRunbook}
        exporting={exporting}
      />
      {viewer}
    </div>
  );
}

export default RamBotBubble;
